import { Box, Typography } from '@mui/material';
import { CompanyTitle } from '../components/CompanyTitle';
import { useDealContext } from '../../deal/context';
import { DisplayBoxBase } from '../../../components/shared/StyledComponents';
import { useEffect, useRef, useState } from 'react';
import { AdvisorGrid } from '../components/advisor/AdvisorGrid';
import { IAdvisor, IProject, IUser, MeetingStatus } from '../../../types';
import { useAdvisors } from '../../advisor/context/AdvisorsContext';
import { useUserContext } from '../../users/context/UserContext';
import { useAuth0 } from '@auth0/auth0-react';
import ClientProvider from '../../../modules/ClientProvider';
import { OutreachPrompts } from '../components/outreach/OutreachPrompts';
import Loading from '../../../components/Loading';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { gridStyle } from '../styles';
import { AdvisorGridItem } from '../components/advisor/AdvisorGridItem';
import { AdvisorLeads } from '../components/advisor/AdvisorLeads';
import { NewAdvisors } from '../components/dashboard/NewAdvisors';
import { UpcomingMeetings } from '../components/dashboard/UpcomingMeetings';
import { ActiveTouchpoints } from '../components/dashboard/ActiveTouchpoints';

const stages = [
    {
        id: "connections",
        labelSingular: "Connection",
        labelPlural: "Connections",
        color: "#f8f9fa",
        progressWidth: "25%"
    },
    {
        id: "advisors",
        labelSingular: "Advisor",
        labelPlural: "Advisors",
        color: "#D3E2F3",
        progressWidth: "30%"
    },
    {
        id: "meetings",
        labelSingular: "Intro",
        labelPlural: "Intros",
        color: "#A6C4E8",
        progressWidth: "20%"
    },
    {
        id: "opportunities",
        labelSingular: "Opportunity",
        labelPlural: "Opportunities",
        color: "#5083C6",
        progressWidth: "15%"
    },
    {
        id: "customers",
        labelSingular: "Customer",
        labelPlural: "Customers",
        color: "#255195",
        progressWidth: "10%"
    }
]

const TriangleContainer = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        const updateHeight = () => {
            if (containerRef.current) {
                setContainerHeight(containerRef.current.offsetHeight);
            }
        };

        updateWidth();
        updateHeight();
        window.addEventListener('resize', updateWidth);
        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateWidth);
            window.removeEventListener('resize', updateHeight);
        };
    }, []);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                height: '400px',
                overflow: 'visible',
                marginRight: '-50px'
            }}
        >
            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                <Triangle containerWidth={containerWidth} containerHeight={containerHeight} />
            </Box>
        </Box>
    );
};

interface TriangleProps {
    containerWidth: number;
    containerHeight: number;
}

const Triangle = ({ containerWidth, containerHeight }: TriangleProps) => {
    const getBorderWidth = (percentage: number) => {
        return `${Math.round(containerWidth * percentage)}px`;
    };

    const getBorderHeight = (percentage: number) => {
        return `${Math.round(containerHeight * percentage)}px`;
    };

    return (
        <Box
            sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '100%',
                height: '100%',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '80%',
                    backgroundColor: 'transparent',
                    borderStyle: 'solid',
                    borderWidth: `${getBorderHeight(0.80)} ${getBorderWidth(0.75)} 0 0`,
                    borderColor: 'transparent #D3E2F3 transparent transparent',
                    transition: 'all 0.3s ease',
                    zIndex: 1
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '100%',
                    backgroundColor: 'transparent',
                    borderStyle: 'solid',
                    borderWidth: `${getBorderHeight(1)} ${getBorderWidth(1)} 0 0`,
                    borderColor: 'transparent #F8F9FA transparent transparent',
                    transition: 'all 0.3s ease'
                }
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '60%',
                        backgroundColor: 'transparent',
                        borderStyle: 'solid',
                        borderWidth: `${getBorderHeight(0.60)} ${getBorderWidth(0.45)} 0 0`,
                        borderColor: 'transparent #A6C4E8 transparent transparent',
                        transition: 'all 0.3s ease',
                        zIndex: 2
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '40%',
                        backgroundColor: 'transparent',
                        borderStyle: 'solid',
                        borderWidth: `${getBorderHeight(0.40)} ${getBorderWidth(0.25)} 0 0`,
                        borderColor: 'transparent #5083C6 transparent transparent',
                        transition: 'all 0.3s ease',
                        zIndex: 3
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: '100%',
                        height: '100%',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '20%',
                            backgroundColor: 'transparent',
                            borderStyle: 'solid',
                            borderWidth: `${getBorderHeight(0.20)} ${getBorderWidth(0.1)} 0 0`,
                            borderColor: 'transparent #255195 transparent transparent',
                            transition: 'all 0.3s ease',
                            zIndex: 5
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

const ResponsiveTriangle = () => (
    <TriangleContainer />
);

const PipelineIndicator = ({ currentStage }: { currentStage: string }) => {

    const currentIndex = stages.findIndex(stage => stage.id === currentStage);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            alignItems: 'center',
            width: '100%',
            marginTop: '20px',
            paddingRight: '100px'
        }}>
            {currentStage === 'start' ? (
                <Box sx={{ flex: '100%', position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                    <Box sx={{
                        position: 'absolute',
                        top: '-12px',
                        transform: 'translateX(50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderBottom: '8px solid #5083C6',
                        left: '-8px'
                    }} />
                    <Typography variant="body1" sx={{
                        color: '#1a5dbd',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        fontSize: '0.75rem'
                    }}>
                        You are here
                    </Typography>
                </Box>
            ) : (
                stages.map((stage, index) => {
                    const isActive = index === currentIndex;
                    const isPast = index < currentIndex;

                    return (
                        <Box key={stage.id} sx={{ flex: stage.progressWidth, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isActive && (
                                <>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: '-12px',
                                        transform: 'translateX(50%)',
                                        width: 0,
                                        height: 0,
                                        borderLeft: '8px solid transparent',
                                        borderRight: '8px solid transparent',
                                        borderBottom: '8px solid #5083C6',
                                        marginRight: '16px'
                                    }} />
                                    <Typography variant="body1" sx={{
                                        color: isPast || isActive ? '#1a5dbd' : '#666',
                                        fontWeight: isActive ? 'bold' : 'normal',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.75rem'
                                    }}>
                                        You are here
                                    </Typography>
                                </>
                            )}
                        </Box>
                    );
                })
            )
            }
        </Box >
    );
};

const StatsBox = ({ stage, value, onClick, isActive }: { stage: string, value: number, onClick: () => void, isActive: boolean }) => {

    const stageProperties = stages.find(s => s.id === stage);
    return (
        <Box sx={{
            backgroundColor: stageProperties?.color,
            height: '20%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px 10px 10px 5px',
            cursor: value > 0 ? 'pointer' : 'default',
        }}
            onClick={value > 0 ? onClick : undefined}
        >
            <DisplayBoxBase className="white" sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                color: value ? '#000000' : '#999',
                border: isActive ? '1px solid #1a5dbd' : '1px solid #c9c9c9',
                boxShadow: isActive ? '0 0 10px 0 rgba(26, 93, 189, 0.2)' : 'none'
            }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: value ? '#1a5dbd' : '#999' }}>
                    {value ? value : "---"}
                </Typography>
                <Typography variant="caption" sx={{ lineHeight: '1' }}>
                    {value == 1 ? stageProperties?.labelSingular : stageProperties?.labelPlural}
                </Typography>
            </DisplayBoxBase>
        </Box>
    );
};

export const CompanyDashboard = () => {
    const { dealContext } = useDealContext();
    const project = dealContext.project;
    const viewerRole = dealContext.role;
    const { advisors, loading, fetchAdvisors } = useAdvisors();
    const [previewAdvisors, setPreviewAdvisors] = useState<IAdvisor[]>([]);
    const { userContext } = useUserContext();
    const { getAccessTokenSilently } = useAuth0();
    const [activeTab, setActiveTab] = useState<string>("contacts");
    const [selectedAdvisors, setSelectedAdvisors] = useState<IAdvisor[]>([]);
    const [allAdvisors, setAllAdvisors] = useState<IAdvisor[]>([]);
    const [loadedPreviewAdvisors, setLoadedPreviewAdvisors] = useState<boolean>(false);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const user = userContext.dbUser;
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    // Check scroll possibility
    const checkScroll = () => {
        if (gridContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = gridContainerRef.current;
            setCanScrollUp(scrollTop > 0);
            setCanScrollDown(scrollTop < scrollHeight - clientHeight);
        }
    };

    // Add scroll event listener
    useEffect(() => {
        const container = gridContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            // Initial check
            checkScroll();
            // Check after content loads/changes
            setTimeout(checkScroll, 100);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScroll);
            }
        };
    }, [selectedAdvisors]);

    // Load regular advisors
    useEffect(() => {
        if (project?.id) {
            fetchAdvisors(project.id);
        }
    }, [project?.id]);

    // Load preview advisors
    useEffect(() => {
        const fetchPreviewAdvisors = async () => {
            if (project?.id) {
                const accessToken = await getAccessTokenSilently();
                const { data, error } = await ClientProvider.provideProjectClient().getPreviewAdvisors(project.id, accessToken);
                if (data) {
                    setPreviewAdvisors(data.advisors);
                }
                setLoadedPreviewAdvisors(true);
            }
        };

        fetchPreviewAdvisors();
    }, [project?.id]);

    // Set initial selected advisors to show all contacts
    useEffect(() => {

        if (!loading && loadedPreviewAdvisors) {
            const combined = [...(advisors || []), ...(previewAdvisors || [])];
            setAllAdvisors(combined);
            setSelectedAdvisors(combined);
        }
    }, [loading, loadedPreviewAdvisors, advisors, previewAdvisors]);

    // Initialize with contacts view when data is ready
    useEffect(() => {
        if (allAdvisors.length > 0 && activeTab === "contacts") {
            setSelectedAdvisors(allAdvisors);
        }
    }, [allAdvisors, activeTab]);

    const advisorsMeeting = allAdvisors?.filter((advisor: any) => advisor?.meetings?.filter((meeting: any) => meeting.status !== MeetingStatus.CANCELLED).length > 0);
    const advisorsOpportunities = allAdvisors?.filter((advisor: any) => advisor?.stage === "opportunity" || advisor?.stage === "customer");
    const advisorsCustomers = allAdvisors?.filter((advisor: any) => advisor?.stage === "customer");

    const handleTabSwitch = (tab: string) => {
        setActiveTab(tab);

        let newAdvisors: IAdvisor[] = [];
        switch (tab) {
            case "contacts":
                newAdvisors = allAdvisors;
                break;
            case "advisors":
                newAdvisors = advisors;
                break;
            case "meetings":
                newAdvisors = advisorsMeeting;
                break;
            case "opportunities":
                newAdvisors = advisorsOpportunities;
                break;
            case "customers":
                newAdvisors = advisorsCustomers;
                break;
            default:
                newAdvisors = allAdvisors;
        }
        setSelectedAdvisors(newAdvisors);
    }

    const handleScroll = (direction: string) => {
        if (gridContainerRef.current) {
            const scrollAmount = 300; // Adjust this value to control scroll distance
            if (direction === "up") {
                gridContainerRef.current.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
            } else {
                gridContainerRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
            }
        }
    }

    // Determine current stage based on advisor counts
    const determineCurrentStage = () => {
        if (advisorsCustomers.length > 0) return 'customers';
        if (advisorsOpportunities.length > 0) return 'opportunities';
        if (advisorsMeeting.length > 0) return 'meetings';
        if (advisors.length > 0) return 'advisors';
        if (allAdvisors.length > 0) return 'connections';
        return 'start';
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Box>
            {advisors.length > 0 ?
                <CompanyTitle viewerRole={viewerRole} project={project} section={`${project.name} Advisors`} subheading={<span>So far, your <strong>{advisors.length}</strong> advisors have generated ...</span>} />
                :
                <CompanyTitle viewerRole={viewerRole} project={project} section={`${project.name} Advisors`} subheading="Start adding advisors to generate insight, intros and opportunities." />
            }

            <Box sx={{ marginBottom: '10px' }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '0',
                    width: '100%',
                    height: '400px',
                    position: 'relative',
                    borderRadius: '10px',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ flex: 1, position: 'relative', overflowY: 'hidden' }}>
                        <ResponsiveTriangle />
                        {allAdvisors.length > 0 ? (
                            <>
                                <Box
                                    ref={gridContainerRef}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        zIndex: 10,
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': {
                                            display: 'none'
                                        },
                                        scrollbarWidth: 'none'
                                    }}
                                >
                                    <Box className="viewpoints-facegrid" sx={{ ...gridStyle, marginRight: '30px' }}>
                                        {selectedAdvisors?.map((advisor: any) => (
                                            <AdvisorGridItem
                                                key={advisor?.uuid}
                                                advisor={advisor}
                                                user={user}
                                                style="face-grid"
                                                isVisible={true}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                                {canScrollUp && (
                                    <Box
                                        onClick={() => handleScroll("up")}
                                        sx={{
                                            position: 'absolute',
                                            top: 20,
                                            right: 10,
                                            cursor: 'pointer',
                                            borderRadius: '50%',
                                            backgroundColor: '#ffffffaa',
                                            padding: '5px',
                                            zIndex: 20,
                                            opacity: canScrollUp ? 1 : 0.3,
                                            pointerEvents: canScrollUp ? 'auto' : 'none',
                                            transition: 'opacity 0.2s ease'
                                        }}
                                    >
                                        <ChevronUp />
                                    </Box>
                                )}
                                {canScrollDown && (
                                    <Box
                                        onClick={() => handleScroll("down")}
                                        sx={{
                                            position: 'absolute',
                                            bottom: 20,
                                            right: 10,
                                            cursor: 'pointer',
                                            borderRadius: '50%',
                                            backgroundColor: '#ffffffaa',
                                            padding: '5px',
                                            zIndex: 20,
                                            opacity: canScrollDown ? 1 : 0.3,
                                            pointerEvents: canScrollDown ? 'auto' : 'none',
                                            transition: 'opacity 0.2s ease'
                                        }}
                                    >
                                        <ChevronDown />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '60%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0',
                                margin: 'auto',
                                borderRadius: '10px',
                                padding: '20px',
                                zIndex: 10,
                            }}>
                                <Typography variant="body1">
                                    New advisors will appear here!
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0',
                        width: '100px',
                        height: '100%',
                    }}>
                        <StatsBox stage="connections" value={allAdvisors.length} onClick={() => handleTabSwitch("contacts")} isActive={activeTab === "contacts"} />
                        <StatsBox stage="advisors" value={advisors.length} onClick={() => handleTabSwitch("advisors")} isActive={activeTab === "advisors"} />
                        <StatsBox stage="meetings" value={advisorsMeeting.length} onClick={() => handleTabSwitch("meetings")} isActive={activeTab === "meetings"} />
                        <StatsBox stage="opportunities" value={advisorsOpportunities.length} onClick={() => handleTabSwitch("opportunities")} isActive={activeTab === "opportunities"} />
                        <StatsBox stage="customers" value={advisorsCustomers.length} onClick={() => handleTabSwitch("customers")} isActive={activeTab === "customers"} />

                    </Box>
                </Box>
                <PipelineIndicator currentStage={determineCurrentStage()} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px", width: "100%", marginTop: "1rem", marginBottom: "4rem", alignItems: "stretch" }}>
                <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
                    <NewAdvisors advisors={advisors} />
                </Box>
                <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
                    <UpcomingMeetings />
                </Box>
            </Box>
            {project.insights_enabled && (
                <Box sx={{ marginBottom: "4rem" }}>
                    <ActiveTouchpoints />
                </Box>
            )}
            <AdvisorLeads project={project} />
            <OutreachPrompts project={project} />
        </Box>
    )
}